import { claimVoiceFocus } from "@/lib/radio/radio-mic-bus";
/**
 * One-handed voice capture for DAR entries.
 *
 * Records a single self-contained media file per memo (start -> stop), so the
 * upload is always decodable by the transcription API. Chunked/timeslice
 * recording is deliberately avoided: only the first fragment carries a
 * container header.
 */

export type DarRecording = {
  blob: Blob;
  mimeType: string;
  durationSec: number;
  startedAt: string;
};

const PREFERRED_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/mpeg",
];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  return PREFERRED_TYPES.find((t) => MediaRecorder.isTypeSupported?.(t));
}

export function extensionFor(mimeType: string): string {
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("mpeg")) return "mp3";
  return "webm";
}

export class DarRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startedAtMs = 0;
  private startedAtIso = "";

  get active() {
    return this.recorder?.state === "recording";
  }

  /** Live microphone stream, for local level metering while recording. */
  get mediaStream(): MediaStream | null {
    return this.stream;
  }

  async start(): Promise<void> {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      throw new Error("This device can't record audio in the browser.");
    }
    // Radio steps aside (stops talking, ducks) while D.A.R./Hey MR own the mic.
    this.releaseFocus = claimVoiceFocus();
    try {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    } catch (e) {
      this.releaseFocus?.();
      this.releaseFocus = null;
      throw e;
    }
    const mimeType = pickMimeType();
    this.recorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);
    this.chunks = [];
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.startedAtMs = Date.now();
    this.startedAtIso = new Date().toISOString();
    this.recorder.start();
  }

  /** Stops the recorder and resolves with one complete audio file. */
  stop(): Promise<DarRecording> {
    return new Promise((resolve, reject) => {
      const rec = this.recorder;
      if (!rec) return reject(new Error("Not recording"));
      rec.onerror = () => reject(new Error("Recording failed"));
      rec.onstop = () => {
        const mimeType = rec.mimeType || "audio/webm";
        const blob = new Blob(this.chunks, { type: mimeType });
        this.cleanup();
        resolve({
          blob,
          mimeType,
          durationSec: Math.max(0, Math.round((Date.now() - this.startedAtMs) / 100) / 10),
          startedAt: this.startedAtIso,
        });
      };
      rec.stop();
    });
  }

  cancel() {
    try {
      if (this.recorder?.state === "recording") this.recorder.stop();
    } catch {
      /* ignore */
    }
    this.cleanup();
  }

  private releaseFocus: (() => void) | null = null;

  private cleanup() {
    this.releaseFocus?.();
    this.releaseFocus = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
  }
}

/** Best-effort one-shot GPS fix; never blocks the recording flow. */
export function quickPosition(timeoutMs = 6000): Promise<GeolocationPosition | null> {
  if (typeof navigator === "undefined" || !navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: GeolocationPosition | null) => {
      if (!done) {
        done = true;
        resolve(v);
      }
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        clearTimeout(timer);
        finish(p);
      },
      () => {
        clearTimeout(timer);
        finish(null);
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30000 },
    );
  });
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    binary += String.fromCharCode(...buf.subarray(i, i + chunk));
  }
  return btoa(binary);
}
